import type { ReactElement } from 'react';
import type { IconRenderProps } from '../types';

export function SnapchatIcon({
	width,
	height,
	active = false,
	'aria-label': ariaLabel = 'Snapchat',
	style,
	customIconColor,
	...rest
}: IconRenderProps): ReactElement {
	const bg = customIconColor?.bgColor ?? '#FFFFFF';
	const g = customIconColor?.glyphColor ?? (active ? '#1E1E1E' : 'rgba(30, 30, 30, 0.48)');

	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			width={width ?? '100%'}
			height={height ?? '100%'}
			viewBox="0 0 40 40"
			fill="none"
			role="img"
			aria-label={ariaLabel}
			style={{ display: 'block', ...style }}
			{...rest}
		>
			<title>Snapchat</title>

			<rect width="40" height="40" rx="8" fill={bg} />

			<g transform="translate(4 4) scale(0.061)">
				{/* Ghost body */}
				<path
					d="M258.3 432c-1.1 0-2.1 0-2.8-.1-.6.1-1.2.1-1.9.1-22.3 0-37.2-10.6-50.4-19.9-9.4-6.7-18.3-13-28.8-14.7-5.1-.8-10.2-1.3-15.1-1.3-8.9 0-15.9 1.4-21 2.4-3.1.6-5.9 1.1-8 1.1-2.2 0-4.9-.5-6-4.3-.9-3-1.5-5.9-2.1-8.7-1.5-7-2.6-11.3-5.3-11.7-28-4.3-44.5-10.7-47.8-18.4q-.45-1.2-.6-2.4c-.1-2.3 1.5-4.3 3.8-4.7 22.2-3.7 42-15.4 58.8-34.9 13-15.1 19.4-29.6 20-31.2 0-.1.1-.1.1-.2 3.2-6.6 3.9-12.2 1.9-16.8-3.6-8.5-15.6-12.3-23.5-14.8-2-.6-3.8-1.2-5.3-1.8-7-2.8-18.5-8.6-17-16.7 1.1-5.9 8.9-10 15.2-10 1.7 0 3.3.3 4.6.9 7.1 3.3 13.5 5 19 5 6.8 0 10.1-2.6 10.9-3.3-.2-3.6-.4-7.5-.7-11.2-1.6-25.5-3.6-57.3 4.5-75.6C185.1 84.2 236.7 80 252 80c.4 0 6.6-.1 6.6-.1h.9c15.3 0 67 4.2 91.3 58.8 8.1 18.2 6.1 50.1 4.5 75.6l-.1 1.2q-.3 5.25-.6 9.9c.8.7 3.8 3.1 9.9 3.3 5.2-.2 11.3-1.9 17.9-5 2.1-1 4.3-1.2 5.9-1.2 2.3 0 4.7.5 6.7 1.3h.1c5.6 2 9.3 6 9.4 10.2.1 3.9-2.8 9.8-17.1 15.4-1.5.6-3.3 1.2-5.3 1.8-7.9 2.5-19.8 6.3-23.5 14.8-2 4.6-1.3 10.3 1.9 16.8 0 .1.1.1.1.2 1 2.3 25.1 57.2 78.8 66.1 2.3.4 3.9 2.4 3.8 4.7 0 .8-.2 1.6-.6 2.5-3.3 7.7-19.8 14-47.8 18.3-2.6.4-3.7 4.7-5.2 11.6-.6 2.9-1.3 5.7-2.1 8.6-.8 2.8-2.6 4.1-5.5 4.1h-.4c-1.9 0-4.6-.3-8-1-5.9-1.2-12.6-2.2-21-2.2-4.9 0-10 .4-15.1 1.3-10.4 1.7-19.3 8-28.7 14.7-13.2 9.7-28.2 20.3-50.5 20.3"
					fill="#FFFFFF"
				/>

				{/* Outline */}
				<path
					d="M259.6 84.5c14.4 0 63.9 3.9 87.2 56.2 7.7 17.2 5.7 48.4 4.1 73.5-.2 4-.5 7.8-.7 11.5l-.1 1.6 1.1 1.2c.4.5 4.4 4.6 13.3 5h.2c5.9-.2 12.5-2 19.7-5.4 1.1-.5 2.5-.7 4-.7 1.7 0 3.5.3 5 .9l.2.1c3.8 1.3 6.4 3.8 6.5 6.1 0 1.3-.9 5.9-14.3 11.1-1.3.5-3 1.1-5 1.7-8.7 2.8-21.8 6.9-26.2 17.3-2.5 5.9-1.8 12.8 2 20.6 1.6 3.7 26.4 59.6 82.2 68.8 0 .3-.1.6-.2.9-.9 2.2-7 9.9-44.4 15.7-5.9.9-7.3 7.5-9 15.1-.6 2.7-1.2 5.5-2.1 8.3-.3.9-.3.9-1.2.9h-.4c-1.7 0-4.2-.3-7.1-.9-5.3-1-12.6-2.3-21.9-2.3-5.2 0-10.5.5-15.9 1.3-11.5 1.9-20.8 8.5-30.6 15.5-13.2 9.4-26.9 19-47.8 19-.9 0-1.8 0-2.7-.1h-.4c-.5 0-1 .1-1.6.1-20.9 0-34.6-9.7-47.8-19-9.9-7-19.2-13.6-30.6-15.5-5.4-.9-10.7-1.3-15.9-1.3-9.3 0-16.6 1.4-21.9 2.5-2.9.6-5.4 1.1-7.1 1.1-1.4 0-1.4-.1-1.7-1.1q-1.2-4.35-2.1-8.4c-1.7-7.6-3.1-14.2-9-15.1C80 364.9 74 357.2 73 355c-.1-.4-.2-.6-.2-.9 55.8-9.2 80.6-65.1 82.2-68.8 3.8-7.8 4.5-14.7 2-20.6-4.4-10.4-17.5-14.6-26.2-17.3-2-.6-3.7-1.2-5-1.7-11.3-4.5-14.7-8.9-14.2-11.7.6-3.1 6-6.3 10.8-6.3 1.1 0 2 .2 2.7.5 7.7 3.6 14.7 5.4 20.9 5.4 9.7 0 14-4.5 14.5-5l1.1-1.2-.1-1.6c-.2-3.7-.4-7.5-.7-11.5-1.6-25.1-3.5-56.3 4.1-73.5 23.3-52.1 72.5-56.1 87.1-56.1.4 0 6.7-.1 6.7-.1.4-.1.6-.1.9-.1"
					fill={g}
				/>
			</g>
		</svg>
	);
}
